import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import NetworkManager from '../app/managers/networkManager';
import { config } from '../app/utils/config';
import { showSuccessToast, showErrorToast } from '../app/utils/helpers';

const initialState = {
    collabAssets: [],
    subcontractorAssets: [],
};

export const assetsSlice = createSlice({
    name: 'assets',
    initialState,
    reducers: {
        setCollabAssets: (state, action) => {
            state.collabAssets = action.payload;
        },
        setSubcontractorAssets: (state, action) => {
            state.subcontractorAssets = action.payload;
        },
        addAsset: (state, action) => {
            state.assetsList.push(action.payload);
        },
        removeAsset: (state, action) => {
            state.assetsList = state.assetsList.filter(
                (asset) => asset.id !== action.payload
            );
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getCollabAssets.fulfilled, (state, action) => {
            state.collabAssets = action.payload.responseData.assets;
        });
        builder.addCase(getSubcontractorAssets.fulfilled, (state, action) => {
            state.subcontractorAssets = action.payload.responseData.assets;
        });
        builder.addCase(uploadAsset.fulfilled, (state, action) => {
            const { responseData } = action.payload;
            if (responseData && responseData.status === 1) {
                const newAsset = { ...action.meta.arg, id: responseData.insert_id };
                state.assetsList.push(newAsset);
                showSuccessToast('Asset uploaded successfully');
            }
        });
        builder.addCase(deleteAsset.fulfilled, (state, action) => {
            const { responseData } = action.payload;
            if (responseData && responseData.status === 1) {
                state.assetsList = state.assetsList.filter(
                    (asset) => asset.id !== action.meta.arg.id
                );
                showSuccessToast('Asset deleted successfully');
            }
        });
    },
});

export const uploadAsset = createAsyncThunk(
    'assets/uploadAsset',
    async ({ file, tableName, idParent, filename }, thunkAPI) => {
        try {
            if(file && file.size > 10000000) {
                showErrorToast("File size should be less than 10MB");
                return thunkAPI.rejectWithValue("File size should be less than 10MB");
            }
            const response = await NetworkManager.getInstance().uploadAssets(file, tableName, idParent, filename);

            if (response) {
                showSuccessToast("File uploaded successfully!");
                return response;
            }
            throw new Error("Upload failed");
        } catch (error) {
            console.error("Error uploading file:", error);
            showErrorToast("Error uploading file");
            return thunkAPI.rejectWithValue(error);
        }
    }
);



export const deleteAsset = createAsyncThunk(
    'assets/deleteAsset',
    async (body, thunkAPI) => {
        try {
            const response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.assets.deleteAsset,
                body
            );
            return response;
        } catch (error) {
            showErrorToast("Error deleting asset");
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const downloadAsset = createAsyncThunk(
    'assets/downloadAsset',
    async ({ tableName, idParent, filename }, thunkAPI) => {
        try {
            const path = `/v1/assets/download/${tableName}/${idParent}/${filename}`;
            const response = await NetworkManager.getInstance().downloadAssetFile(path, filename);
            console.log("Download response:", response);
        } catch (error) {
            console.error("Error downloading file:", error);
            showErrorToast("Error downloading asset");
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const getCollabAssets = createAsyncThunk(
    'assets/getCollabAssets',
    async (body, thunkAPI) => {
        try {
            const response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.assets.getCollabAssets,
                body
            );
            return response;
        } catch (error) {
            showErrorToast("Error fetching assets");
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const getSubcontractorAssets = createAsyncThunk(
    'assets/getSubcontractorAssets',
    async (body, thunkAPI) => {
        try {
            const response = await NetworkManager.getInstance().postRequest(
                config.apiPaths.assets.getSubcontractorAssets,
                body
            );
            return response;
        } catch (error) {
            showErrorToast("Error fetching assets");
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const { setCollabAssets, setSubcontractorAssets, addAsset, removeAsset } = assetsSlice.actions;

export default assetsSlice.reducer;
